/**
 * The minimal map shape the assistant map-key helpers read. Both the hub's and
 * the admin's `IMap` structurally satisfy this, so the helpers stay the single
 * source of truth without depending on either app's full type.
 *
 * Note: the hub's `IMap` also carries an unrelated `mapName` field (a UE asset
 * name used by streaming/teleport) — it is deliberately not read here.
 */
export interface IAssistantMapRef {
  ueIdentifier?: string | null;
  name?: string | null;
}

/** A map to scope assistant content by, or nothing when unscoped. */
export type AssistantMapRef = IAssistantMapRef | null | undefined;

const normalize = (value?: string | null): string => (value ?? '').trim();

/**
 * Resolves the canonical value to store in an assistant record's `mapName`
 * (`IAssistantScript.mapName`, `IAssistantRagVectorStorage.mapName`).
 *
 * The canonical key is `map.ueIdentifier` — stable for the lifetime of a
 * build, whereas `map.name` is a display name an admin renames at will, and a
 * rename orphans every script and knowledge item bound to the old name
 * (MV-1891). Falls back to `map.name` only when the map carries no
 * `ueIdentifier` at all, so a write can never produce an empty key.
 *
 * This is the single source of the value on every write path — never read
 * `map.name` directly when saving assistant content.
 *
 * @param map - Map record the assistant content is being linked to.
 * @returns The key, or `undefined` when neither field carries a value.
 */
export const assistantMapKey = (map?: AssistantMapRef): string | undefined => {
  if (!map) {
    return undefined;
  }

  return normalize(map.ueIdentifier) || normalize(map.name) || undefined;
};

/**
 * Whether an assistant record's stored `mapName` refers to the given map.
 *
 * Matches case-insensitively against `map.ueIdentifier` (the canonical key
 * written by `assistantMapKey`) **or** `map.name` (the display name older
 * records were written with).
 *
 * ⚠️ The `map.name` fallback is temporary. It exists only so a release can read
 * both the pre- and post-migration form of the data, per the expand/contract
 * rule in `spatial-manager/src/migrations/README.md`. Drop it in a separate
 * ticket once the `mapName` → `ueIdentifier` migration has run on every
 * environment.
 *
 * @param rowMapName - `mapName` as stored on the script / knowledge record.
 * @param map - Map record to scope by.
 * @returns `true` when the record belongs to that map; `false` for a blank
 * `rowMapName` or a missing map (callers decide what an unscoped record means).
 */
export const matchesAssistantMap = (
  rowMapName?: string | null,
  map?: AssistantMapRef,
): boolean => {
  const row = normalize(rowMapName).toLowerCase();

  if (!row || !map) {
    return false;
  }

  const ueIdentifier = normalize(map.ueIdentifier).toLowerCase();
  const name = normalize(map.name).toLowerCase();

  return (!!ueIdentifier && row === ueIdentifier) || (!!name && row === name);
};
